import { Check, Zap, Brain, BookOpen } from 'lucide-react';

// Tier metadata maps model-id keywords to a tier label + colour.
// Falls back to "Standard" if no keyword matches.
function tierMeta(model) {
  const id = (model.id || '').toLowerCase();
  const name = (model.name || '').toLowerCase();
  const haystack = `${id} ${name}`;
  if (/(opus|gpt-4|gpt-5|ultra|pro|premium|flagship)/.test(haystack)) {
    return { label: 'Stärkste', cls: 'tier-flagship' };
  }
  if (/(haiku|nano|flash|mini|fast|3\.5-haiku|3-5-haiku)/.test(haystack)) {
    return { label: 'Schnellste', cls: 'tier-fast' };
  }
  if (/(sonnet|standard|balanced)/.test(haystack)) {
    return { label: 'Empfohlen', cls: '' };
  }
  return { label: 'Modell', cls: '' };
}

// Heuristic dot-meters from model name. We can't introspect benchmarks here,
// so this is a content-aware approximation users can recognise at a glance.
function dotsFor(model) {
  const id = (model.id || '').toLowerCase();
  const name = (model.name || '').toLowerCase();
  const haystack = `${id} ${name}`;
  if (/(opus|ultra|premium|pro|flagship|gpt-5)/.test(haystack)) return { speed: 2, brain: 5 };
  if (/(haiku|nano|flash|mini|fast)/.test(haystack))            return { speed: 5, brain: 3 };
  if (/(sonnet|standard|balanced|gpt-4)/.test(haystack))         return { speed: 4, brain: 4 };
  return { speed: 3, brain: 3 };
}

export default function ModelSelector({ provider, selectedModel, onSelect }) {
  // For Ollama, models are managed separately in OllamaConfig
  if (provider.id === 'ollama') {
    return (
      <p className="settings-section-desc" style={{ margin: 0 }}>
        Ollama-Modelle werden weiter unten in der Ollama-Konfiguration verwaltet.
      </p>
    );
  }

  const models = provider.models || [];
  if (models.length === 0) {
    return (
      <p className="settings-section-desc" style={{ margin: 0 }}>
        Für diesen Anbieter sind keine Modelle verfügbar.
      </p>
    );
  }

  return (
    <div className="model-grid" role="radiogroup" aria-label={`Modell für ${provider.name}`}>
      {models.map((model) => {
        const tier = tierMeta(model);
        const dots = dotsFor(model);
        const isSelected = model.id === selectedModel;
        return (
          <button
            key={model.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(model.id)}
            className={`model-card ${isSelected ? 'is-selected' : ''}`}
          >
            <header className="model-card-head">
              <div>
                <div className="model-tier">
                  <span className={`tier-badge ${tier.cls}`}>{tier.label}</span>
                </div>
                <div className="model-name">{model.name}</div>
              </div>
              <span className="model-mark" aria-hidden="true">
                <Check size={12} />
              </span>
            </header>
            {model.description && <p className="model-desc">{model.description}</p>}
            <div className="model-meters">
              <Meter label="Geschwindigkeit" Icon={Zap} level={dots.speed} />
              <Meter label="Reasoning" Icon={Brain} level={dots.brain} />
            </div>
            <footer className="model-foot">
              {model.contextWindow && (
                <span className="model-meta">
                  <BookOpen size={13} />
                  <span>Kontext: <b>{formatContext(model.contextWindow)}</b></span>
                </span>
              )}
              {model.pricePerMillion && (
                <span className="model-meta">
                  <span>≈ <b>{model.pricePerMillion}</b> / 1M Token</span>
                </span>
              )}
            </footer>
          </button>
        );
      })}
    </div>
  );
}

function Meter({ label, Icon, level }) {
  return (
    <div className="meter">
      <span className="meter-label">
        <Icon size={14} />
        <span>{label}</span>
      </span>
      <span className="meter-dots" aria-label={`${label}: ${level} von 5`}>
        {[1, 2, 3, 4, 5].map((i) => (
          <span key={i} className={`meter-dot ${i <= level ? 'is-on' : ''}`} />
        ))}
      </span>
    </div>
  );
}

function formatContext(n) {
  if (typeof n !== 'number') return n;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace('.0', '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return String(n);
}
